import { createAsyncThunk } from "@reduxjs/toolkit";
import { fetchData } from "../../utils";
import { setNotification } from "../notificationsSlice";
import { has, toLower } from "lodash";
import { API_SUPPORT_ISSUE, API_SUPPORT_ISSUE_ATTACHMENT, API_SUPPORT_ISSUE_COMMENT } from "../../settings";
import { schemas } from '../schemas';


export const getIssueDetails = createAsyncThunk(
    "getIssueDetails",
    async(issue, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(API_SUPPORT_ISSUE, {
                headers: { "X-Ticket-Ref": toLower(issue) }
            }, schemas.ISSUE);
            if (has(response, "entities") && has(response.entities, "issues")) {
                dispatch(getIssueComments(issue));
                dispatch(getIssueAttachments(issue));
                return response.entities.issues[toLower(issue)];
            };
        } catch (e) {
            dispatch(setNotification(e));
            return rejectWithValue({ code: e.code });
        };
        return rejectWithValue({});
    }
);

export const getIssueComments = createAsyncThunk(
    "getIssueComments",
    async(issue, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(API_SUPPORT_ISSUE + "/comments", {
                headers: {
                    "X-Ticket-Ref": issue
                }
            }, schemas.ISSUE_COMMENTS);
            return response.entities.hasOwnProperty("comments")
                ?  Object.values(response.entities.comments)
                : [];
        } catch (e) {
            dispatch(setNotification(e));
        };
        return rejectWithValue([]);
    }
);

export const getIssueAttachments = createAsyncThunk(
    "getIssueAttachments",
    async(issue, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(API_SUPPORT_ISSUE + "/attachments", {
                headers: {
                    "X-Ticket-Ref": issue
                }
            }, {});
            return response.hasOwnProperty("result")
                ?  Object.values(response.result)
                : [];
        } catch (e) {
            dispatch(setNotification(e));
        };
        return rejectWithValue([]);
    }
);

export const downloadAttachment = createAsyncThunk(
    "downloadAttachment",
    async({ issue, id }, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(API_SUPPORT_ISSUE_ATTACHMENT + "/download", {
                headers: {
                    "X-Ticket-Ref": issue,
                    "X-Attachment-Id": id
                }
            }, {});
            if (response.result && response.result.url) {
                let a = document.createElement("a");
                a.href = response.result.url;
                a.rel = "noopener noreferrer";
                a.target = "_blank"
                a.click();
                a.remove();
            };
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);

export const deleteAttachment = createAsyncThunk(
    "deleteAttachment",
    async({ issue, id }, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(API_SUPPORT_ISSUE_ATTACHMENT, {
                method: "delete",
                headers: {
                    "X-Ticket-Ref": issue,
                    "X-Attachment-Id": id
                }
            }, {});
            dispatch(setNotification(response.result));
            return response.result
        } catch (e) {
            dispatch(setNotification(e));
            return rejectWithValue(e);
        };
    }
);

export const updateAttachment = createAsyncThunk(
    "updateAttachment",
    async({ issue, id, visible }, { dispatch }) => {
        try {
            const response = await fetchData(API_SUPPORT_ISSUE_ATTACHMENT + "/update", {
                method: "put",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    "ticket_ref": issue,
                    "attachment_id": id,
                    "visible": visible
                })
            }, {});
            if (response.result && response.result.success) {
                dispatch(getIssueAttachments(issue));
            };
            dispatch(setNotification(response.result));
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);

export const addAttachment = createAsyncThunk(
    "addAttachment",
    async({ issue, files=[], visible }, { dispatch }) => {
        try {
            const fmData = new FormData();
            fmData.append("data", JSON.stringify({
                issue: issue,
                visible: visible
            }));
            for (const file of files) {
                fmData.append("attachment", file);
            };
            const response = await fetchData(API_SUPPORT_ISSUE_ATTACHMENT, {
                method: "put",
                body: fmData
            });
            dispatch(getIssueAttachments(issue));
            dispatch(setNotification(response.result));
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);

export const addIssueComment = createAsyncThunk(
    "addIssueComment",
    async({ issue, comment, visible }, { dispatch }) => {
        try {
            const response = await fetchData(API_SUPPORT_ISSUE_COMMENT, {
                method: "put",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    issue: issue,
                    comment: comment,
                    visible: visible
                })
            }, {});
            dispatch(setNotification(response.result));
            dispatch(getIssueComments(issue));
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);
